import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Alert, Button, Group, Modal, Text } from "@mantine/core"
import { useNavigate } from "@tanstack/react-router"
import { useCallback, useEffect, useMemo, useState } from "react"
import { displayMeetingTitle } from "../../lib/meeting-title"
import { FinalizationProgress } from "../FinalizationProgress"
import type { IFocusArgs } from "../RecordingControls/RecordingControlsView/interfaces"
import { UNANSWERED_SPEECH_STALE_SEC } from "./hearing"
import { LanguagePill } from "./LanguagePill"
import { LanguageSwitchPrompt } from "./LanguageSwitchPrompt"
import classes from "./LiteRecordingFocus.module.css"
import { RecordingBar } from "./RecordingBar"
import { SpeakerHypothesisStrip } from "./SpeakerHypothesisStrip"
import { Topbar } from "./Topbar"
import { TranscriptCard } from "./TranscriptCard"

/**
 * Lite live-recording surface — timer + stop, who's speaking, and the live
 * transcript. No coach, no pause/snap/boost, no tone picker, no running summary.
 */
export function LiteRecordingFocus({
  paused,
  finalizing = false,
  error = null,
  onDismissError,
  wsReconnecting = false,
  health = "nominal",
  healthIssues = [],
  processingStage,
  subject,
  startTime,
  elapsed,
  level,
  audio,
  segments,
  interimText,
  interimSpeaker,
  transcriptStaleSec = 0,
  onRequestFinish,
  onRename,
  speakerHypotheses = [],
  hypothesisPending,
  onHypothesisAction,
  languageCode,
  languageSignal = null,
  languageIsWorkspaceDefault = false,
  languageSuggestion = null,
  onAcceptLanguageSwitch,
  onKeepCurrentLanguage,
}: IFocusArgs): React.JSX.Element {
  const [exitConfirmOpen, setExitConfirmOpen] = useState(false)

  const { i18n } = useLingui()
  const navigate = useNavigate()

  const blankSubject = subject.trim() === ""
  const displaySubject = useMemo((): string => {
    if (!blankSubject) return subject
    return displayMeetingTitle(subject, null, i18n, { lite: true, startedAt: startTime })
  }, [blankSubject, subject, startTime, i18n])

  const handleExitRequest = useCallback((): void => {
    setExitConfirmOpen(true)
  }, [])

  const handleExitConfirm = useCallback((): void => {
    setExitConfirmOpen(false)
    navigate({ to: "/" }).catch(() => undefined)
  }, [navigate])

  useEffect(() => {
    if (finalizing) return
    const handler = (e: KeyboardEvent): void => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return
      if (e.target instanceof HTMLElement && e.target.isContentEditable) return
      if (e.key === "Escape") {
        e.preventDefault()
        setExitConfirmOpen(true)
      }
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [finalizing])

  const topbarPhase = finalizing ? "finalizing" : paused ? "paused" : "recording"

  const languagePill = (
    <LanguagePill
      languageCode={languageCode}
      languageSignal={languageSignal}
      languageIsWorkspaceDefault={languageIsWorkspaceDefault}
    />
  )
  const unanswered = !paused && !finalizing && transcriptStaleSec >= UNANSWERED_SPEECH_STALE_SEC

  return (
    <div className={classes.shell} data-testid="lite-recording-focus">
      <Topbar
        phase={topbarPhase}
        subject={displaySubject}
        startTime={blankSubject ? null : startTime}
        onRename={onRename}
        onExit={handleExitRequest}
        exitLabel={<Trans>Meetings</Trans>}
        hint={blankSubject ? <Trans>Gets a title when you stop</Trans> : undefined}
        health={health}
        wsReconnecting={wsReconnecting}
        healthIssues={healthIssues}
      />

      {error ? (
        <Alert
          color="danger"
          variant="light"
          title={<Trans>Something went wrong</Trans>}
          withCloseButton={Boolean(onDismissError)}
          onClose={onDismissError}
          mb="md"
          data-testid="recording-error"
        >
          <Text size="sm" mb={4}>
            {error}
          </Text>
          <Text size="xs" c="dimmed">
            <Trans>Your captured audio is safe — nothing was lost.</Trans>
          </Text>
        </Alert>
      ) : null}

      {languageSuggestion && onAcceptLanguageSwitch && onKeepCurrentLanguage ? (
        <LanguageSwitchPrompt
          currentCode={languageSuggestion.current}
          candidateCode={languageSuggestion.candidate}
          onSwitch={onAcceptLanguageSwitch}
          onKeep={onKeepCurrentLanguage}
        />
      ) : null}

      <div className={classes.cockpit}>
        {finalizing ? (
          <div className={classes.finalizeCard}>
            <FinalizationProgress stage={processingStage ?? null} mode="lite" />
          </div>
        ) : (
          <RecordingBar
            paused={paused}
            elapsed={elapsed}
            level={level}
            audio={audio}
            onPause={() => undefined}
            onResume={() => undefined}
            onFinish={onRequestFinish}
            compact
            languagePill={languagePill}
          />
        )}

        <div className={classes.liveBody}>
          <div className={classes.col}>
            <SpeakerHypothesisStrip
              heading={<Trans>Who's speaking</Trans>}
              hypotheses={speakerHypotheses}
              pending={hypothesisPending}
              onAction={onHypothesisAction}
            />
            {unanswered ? (
              <p className={classes.staleNotice} role="status" data-testid="transcript-stale">
                <Trans>We hear you, but no words are coming through. Check the microphone.</Trans>
              </p>
            ) : null}
            <TranscriptCard
              segments={segments}
              interimText={interimText}
              interimSpeaker={interimSpeaker}
              paused={paused}
              micClipping={level === "clipping"}
              fill
            />
          </div>
        </div>
      </div>

      <Modal
        opened={exitConfirmOpen}
        onClose={() => setExitConfirmOpen(false)}
        title={i18n._(msg`Leave the recording?`)}
        centered
      >
        <Text size="sm" c="dimmed" mb="md">
          <Trans>
            The recording will keep running in this tab as long as you stay on the page. Leave to come back to the
            meeting list — your audio is still saving.
          </Trans>
        </Text>
        <Group justify="flex-end">
          <Button variant="subtle" onClick={() => setExitConfirmOpen(false)}>
            <Trans>Stay here</Trans>
          </Button>
          <Button color="danger" onClick={handleExitConfirm}>
            <Trans>Leave anyway</Trans>
          </Button>
        </Group>
      </Modal>
    </div>
  )
}
